/* eslint-disable import/no-cycle */
import { action, observable } from 'mobx';
import ApiRoutes from 'routes/api/ApiRoutes';
import { ModelItem } from 'models/ModelItem';
import { RecertificationApplicantSteps } from 'models/RecertificationApplicantStep';
import { DocumentStatus } from 'enums/DocumentStatus.enum';
import { notification } from 'antd';
import Store from './Store';
import { RecertificationApplicant } from '../models/entities/RecertificationApplicantModel';
import { PaginatedModelList } from '../models/PaginatedModelList';

export class RecertificationApplicantStore extends Store<
  RecertificationApplicant
> {
  constructor() {
    super();
    RecertificationApplicant._store = this;
  }

  @observable recertificationApplicant = new ModelItem<
    RecertificationApplicant
  >(RecertificationApplicant);

  @observable
  recertificationApplicantsPaginatedList = new PaginatedModelList<
    RecertificationApplicant
  >(RecertificationApplicant);

  @observable currentStep: RecertificationApplicantSteps;

  // eslint-disable-next-line no-return-assign
  @action setCurrentStep = (step: RecertificationApplicantSteps) =>
    (this.currentStep = step);

  @action
  async fetchMonitoringTenants(params?: { [key: string]: any }): Promise<void> {
    return this.recertificationApplicantsPaginatedList.load(
      ApiRoutes.tenantMonitoring.list,
      params,
    );
  }

  @action
  clearStore(): void {
    this.currentStep = null;
    this.recertificationApplicantsPaginatedList = new PaginatedModelList<
      RecertificationApplicant
    >(RecertificationApplicant);
  }

  @action
  async removeTenantMxMonitoring(
    tenant: RecertificationApplicant,
  ): Promise<void> {
    const response = await this.apiService.patch(
      ApiRoutes.tenantMonitoring.remove(tenant.id),
    );
    if (response) {
      this.recertificationApplicantsPaginatedList.removeItem(tenant);
    }
    return response;
  }

  @action async reGenerateReport(tenant: RecertificationApplicant) {
    const response = await this.apiService.post(
      ApiRoutes.tenantMonitoring.regenerate(tenant.id),
    );

    if (response) {
      (this.recertificationApplicantsPaginatedList.items as any).forEach(
        (applicant) => {
          if (applicant.id === tenant.id) {
            // tenant.report_url = response
            // applicant.report_url = response?.data?.report_url;
            // applicant.last_updated = response?.data?.last_updated;
          }
        },
      );
    }

    return response;
  }

  @action
  async completeRecertificationApplicantDocumentUpload(
    recertificationApplicantId: number,
    documentId: number,
    uploadCount?: number,
  ) {
    await this.apiService.post(
      ApiRoutes.recertificationApplicantDocument.complete(
        recertificationApplicantId,
        documentId,
      ),
      {
        upload_count: uploadCount,
      },
    );
  }

  @action
  async getRecertificationApplicantDocuments(
    recertificationApplicantId: number,
    status: DocumentStatus,
  ) {
    const response = await this.apiService.get(
      ApiRoutes.recertificationApplicantDocument.list(
        recertificationApplicantId,
      ),
      { status },
    );
    return response;
  }

  @action
  async getExistingApplicationDocumentSignedUrl(
    recertificationApplicantId: number,
    docId: number,
    file: File,
  ): Promise<any> {
    const body = {
      file_name: file.name,
      content_type: file.type,
    };
    return this.apiService.post(
      ApiRoutes.recertificationApplicantDocument.signedUrlForExisting(
        recertificationApplicantId,
        docId,
      ),
      body,
    );
  }

  @action
  async confirmDocumentUpload(
    recertificationApplicantId: number,
    docId: number,
    renterDocumentId: number,
  ): Promise<void> {
    await this.apiService.put(
      ApiRoutes.recertificationApplicantDocument.confirmUpload(
        recertificationApplicantId,
        docId,
        renterDocumentId,
      ),
    );
  }

  @action
  async getNewApplicationDocumentSignedUrl(
    file: File,
    recertificationApplicantId: number,
    userId: number,
    docType: string,
  ): Promise<any> {
    const body = {
      file_name: file.name,
      content_type: file.type,
      user_id: userId,
      doc_type: docType,
    };

    const response = await this.apiService.post(
      ApiRoutes.recertificationApplicantDocument.signedUrlForNew(
        recertificationApplicantId,
      ),
      body,
    );
    return response;
  }

  @action
  async requestDocument(
    docName: string,
    request_description: string,
    userId: number,
    recertification_applicant_id: number,
    upload_against?: number,
  ): Promise<any> {
    const response = await this.apiService.post(
      ApiRoutes.recertificationApplicantDocument.list(
        recertification_applicant_id,
      ),
      {
        user_id: userId,
        request_description,
        request_message: docName,
        upload_against,
      },
    );
    if (response.data) {
      notification.success({
        message: `${docName} document requested successfully.`,
        duration: 2,
      });
    }
    return response;
  }

  async deleteDocRequestAndProcess(
    recertificationApplicantId: number,
    userId: number,
    requestMessage: string,
  ): Promise<void> {
    await this.apiService.post(
      ApiRoutes.recertificationApplicantDocument.deleteRequestAndProcess(
        recertificationApplicantId,
      ),
      { userId, requestMessage },
    );
  }

  async deleteDocRequest(
    recertificationApplicantId: number,
    userId: number,
    requestMessage: string,
  ): Promise<void> {
    await this.apiService.post(
      ApiRoutes.recertificationApplicantDocument.deleteRequest(
        recertificationApplicantId,
      ),
      { userId, requestMessage },
    );
  }

  @action
  async updateAdditionalInfo(applicantId: number, data: any) {
    try {
      await this.apiService.post(
        ApiRoutes.tenantMonitoring.updateAdditionalInfo(applicantId),
        data,
      );
    } catch (e) {
      notification.error({
        message: (e as Error)?.message || 'Something went wrong!',
        duration: 2.5,
      });
    }
  }

  async moveApplicationToRecertification(applicationId: number) {
    const response = await this.apiService.post(
      ApiRoutes.recertificationApplicantDocument.moveApplicationToRecertification(
        applicationId,
      ),
    );
    return response;
  }
}
